import { useState } from 'react';

import { isOnboardingActive, isOnboardingSurvey } from 'entities';

import { Box, Button, Column, Modal, Row, Tag } from 'components/Blocks';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PartyIcon,
  PhoneIcon,
  TargetIcon,
} from 'components/Blocks/Icons';

import { Trans, useLang } from 'locale';
import { cn } from 'styles';

import { useDashboard } from 'providers/Auth';

import { getLastIndex } from 'utils/array';
import { store } from 'config/storage';
import { openOnNewTab } from 'utils/html';
import { appConfig } from 'config/app';

interface OnboardingModalProps {
  onSurveyClick(id: string): void;
}

const steps = [
  {
    Icon: TargetIcon,
    title: 'onboarding.welcome-title',
    description: [
      'onboarding.welcome-desc-1',
      'onboarding.welcome-desc-2',
      'onboarding.welcome-desc-3',
    ],
    Decoration: () => (
      <p className="font-semibold text-primary">
        <Trans textKey="onboarding.welcome-decoration" />
      </p>
    ),
  },

  {
    Icon: PhoneIcon,
    title: 'onboarding.more-surveys-title',
    description: ['onboarding.more-surveys-desc-1'],
    Decoration: (params: { points: number }) => (
      <p className="flex flex-wrap items-center justify-center gap-0.5">
        <ArrowRightIcon size={16} color="primary" className="mr-1" />
        <Trans textKey="onboarding.more-surveys-decoration" params={params} />
      </p>
    ),
  },

  {
    Icon: PartyIcon,
    title: 'onboarding.ready-title',
    description: ['onboarding.ready-desc-1', 'onboarding.ready-desc-2'],
  },
];

export function OnboardingModal({ onSurveyClick }: OnboardingModalProps) {
  const [{ surveys }] = useDashboard();
  const { t } = useLang();

  const [isOpen, setState] = useState(() => {
    if (!isOnboardingActive(surveys)) return false;

    const wasShown = store.onboardingShown.get();

    return !wasShown;
  });

  const [step, setStep] = useState(0);

  if (!isOpen) return null;

  const back = () => {
    setStep((old) => Math.max(old - 1, 0));
  };

  const isLast = getLastIndex(steps) === step;

  const closeForever = () => {
    setState(false);
    store.onboardingShown.set(true);
  };

  const onboardingSurvey = surveys.find(isOnboardingSurvey);

  const next = () => {
    if (!isLast) return setStep((old) => Math.min(old + 1, getLastIndex(steps)));

    if (onboardingSurvey) {
      openOnNewTab(onboardingSurvey.url, onboardingSurvey.id);
      onSurveyClick(onboardingSurvey.id);
    }

    closeForever();
  };

  const { description, Icon, title, Decoration } = steps[step];

  const points = onboardingSurvey?.incentives ?? 25;

  return (
    <Modal isOpen={isOpen} close={closeForever} allowEscape={false} width="min(90vw, 520px)">
      <Column
        alignItems="center"
        className="relative text-center duration-500 animate-in fade-in-0"
        key={step}
      >
        {isLast && (
          <Button variant="ghost" className="absolute -left-4 -top-2" color="gray" onClick={back}>
            <ArrowLeftIcon /> {t('onboarding.previous')}
          </Button>
        )}

        <Row justifyContent="center" gap="2">
          {steps.map((_, index) => (
            <div
              key={`step-${index}`}
              className={cn(
                'h-1.5 rounded-full transition-width duration-300',
                index === step ? 'w-16 bg-primary' : 'w-8 bg-secondary',
              )}
            />
          ))}
        </Row>

        {!step && (
          <Tag color="warning" size="medium" className="z-10 -mb-6">
            +{points} {t('points')}
          </Tag>
        )}

        <Box className="relative rounded-full bg-primary/10 text-primary" size={80}>
          <Icon size={40} />
        </Box>

        <h2 className="text-xl text-foreground-title">{t(title, { appName: appConfig.name })}</h2>

        {description.map((desc) => (
          <p key={desc} className="-mt-1 text-center">
            {t(desc, { points })}
          </p>
        ))}

        {Decoration && <Decoration points={points} />}

        {!step || isLast ? (
          <Button className="w-full" onClick={next}>
            {step ? t('onboarding.start-now') : t('onboarding.next')}
          </Button>
        ) : (
          <Row justifyContent="space-between" fullWidth>
            <Button color="gray" variant="outlined" onClick={back} size="large">
              {t('onboarding.previous')}
            </Button>

            <Button onClick={next} size="large">
              {t('onboarding.next')}
            </Button>
          </Row>
        )}
      </Column>
    </Modal>
  );
}
